import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { ApiKey } from '../services/ApiKeysService';

interface ApiKeyTokenDialogProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
}

const ApiKeyTokenDialog: React.FC<ApiKeyTokenDialogProps> = ({
  open,
  apiKey,
  onClose
}) => {
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopy = async () => {
    if (!apiKey?.token) return;

    try {
      await navigator.clipboard.writeText(apiKey.token);
      setCopied(true);
      setSnackbarOpen(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = apiKey.token;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setSnackbarOpen(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setCopied(false);
    setSnackbarOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VpnKeyIcon color="success" sx={{ mr: 1.5 }} />
            <Typography variant="h6" component="span">
              API Key Created
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save this API key token now. For security reasons, it will not be shown again.
          </Alert>

          {apiKey && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Key Name: <strong>{apiKey.name}</strong>
              </Typography>
              <TextField
                fullWidth
                label="API Key Token"
                value={apiKey.token || ''}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                          <IconButton onClick={handleCopy} edge="end">
                            {copied ? (
                              <CheckIcon color="success" />
                            ) : (
                              <ContentCopyIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              minWidth: '80px',
              fontWeight: 600
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="API key token copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ApiKeyTokenDialog;
